import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import {alpha} from '@mui/material/styles';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {imageSource} from './images';
import {AgentEditorAccess, useAgentConversation} from './useAgentConversation';
import type {AgentImage} from './types';

interface Props extends AgentEditorAccess {
    open: boolean;
    canEdit: boolean;
    onClose: () => void;
    selectedLabels: string[];
}

export default function AutomationAgentPanel({open, canEdit, onClose, selectedLabels, ...access}: Props) {
    const {t} = useTranslation();
    const chat = useAgentConversation(access);
    const {enqueueSnackbar} = useSnackbar();
    const fileInput = useRef<HTMLInputElement>(null);
    const composer = useRef<HTMLTextAreaElement>(null);
    const conversation = useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = useState(false);
    const dragDepth = useRef(0);
    const [preview, setPreview] = useState<AgentImage | null>(null);
    const canAttach = canEdit && !chat.busy && !chat.readingImages;
    // The repair turn's messages are sent with the conversation but never shown.
    const visible = chat.messages.filter(message => !message.hidden);

    useEffect(() => {
        if (open) composer.current?.focus({preventScroll: true});
    }, [open]);

    const attachImages = async (files: File[]) => {
        if (!canAttach) return;
        const warning = await chat.attach(files);
        if (warning) enqueueSnackbar(warning, {variant: 'warning', preventDuplicate: true});
        composer.current?.focus({preventScroll: true});
    };

    useEffect(() => {
        if (open && conversation.current) conversation.current.scrollTop = conversation.current.scrollHeight;
    }, [open, chat.messages, chat.busy, chat.error, chat.activity, chat.proposal]);

    const chooseAnswer = (question: string, answer: string) => {
        chat.setText(chat.text ? `${chat.text}\n${question} ${answer}` : `${question} ${answer}`);
        composer.current?.focus();
    };

    const thumbnails = (images: AgentImage[], removable = false) => (
        <Stack direction="row" gap={1} flexWrap="wrap">
            {images.map(image => (
                <Box key={image.id} sx={{position: 'relative'}}>
                    <Box component="button" type="button" aria-label={t('View {{name}}', {name: image.name})} onClick={() => setPreview(image)}
                         sx={{p: 0, width: 88, height: 64, border: 1, borderColor: 'divider', borderRadius: 1,
                             overflow: 'hidden', cursor: 'pointer', bgcolor: 'background.paper', display: 'block'}}>
                        <Box component="img" src={imageSource(image)} alt={image.name} sx={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                    </Box>
                    {removable && <IconButton size="small" aria-label={t('Remove {{name}}', {name: image.name})} onClick={() => chat.removeImage(image.id)}
                        sx={{position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', border: 1, borderColor: 'divider', p: 0.25}}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>}
                </Box>
            ))}
        </Stack>
    );

    return (
        <Box component="aside" aria-label={t('Automation agent')} id="automation-agent-panel"
             onDragEnter={event => {if (event.dataTransfer.types.includes('Files')) {event.preventDefault(); dragDepth.current += 1; if (canAttach) setDragging(true);}}}
             onDragOver={event => {if (event.dataTransfer.types.includes('Files')) {event.preventDefault(); event.dataTransfer.dropEffect = canAttach ? 'copy' : 'none';}}}
             onDragLeave={event => {if (event.dataTransfer.types.includes('Files')) {dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false);}}}
             onDrop={event => {if (event.dataTransfer.files.length) {event.preventDefault(); dragDepth.current = 0; setDragging(false); void attachImages(Array.from(event.dataTransfer.files));}}}
             sx={{display: open ? 'flex' : 'none', flexDirection: 'column', width: {xs: 'min(380px, 100%)', md: 380},
                 flexShrink: 0, minHeight: 0, bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider',
                 position: {xs: 'absolute', md: 'relative'}, inset: {xs: '0 auto 0 0', md: 'auto'}, zIndex: 6}}>
            {dragging && canAttach && <Stack role="status" alignItems="center" justifyContent="center" gap={1}
                sx={{position: 'absolute', inset: 8, zIndex: 2, pointerEvents: 'none', border: '2px dashed',
                    borderColor: 'primary.main', borderRadius: 1, bgcolor: theme => alpha(theme.palette.background.paper, 0.96)}}>
                <AddPhotoAlternateOutlinedIcon color="primary"/>
                <Typography sx={{fontSize: 14}}>{t('Drop image here')}</Typography>
            </Stack>}
            <Stack direction="row" alignItems="center" gap={1} sx={{px: 2, py: 1, borderBottom: 1, borderColor: 'divider'}}>
                <Typography variant="subtitle2" sx={{flex: 1}}>{t('Automation agent')}</Typography>
                <Tooltip title={t('New conversation')}><span><IconButton size="small" aria-label={t('New conversation')} onClick={chat.reset}
                    disabled={chat.busy || chat.readingImages || (!chat.messages.length && !chat.images.length && !chat.text)}><RestartAltIcon/></IconButton></span></Tooltip>
                <IconButton size="small" aria-label={t('Close agent')} onClick={onClose}><CloseIcon/></IconButton>
            </Stack>

            <Box ref={conversation} sx={{flex: 1, minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable', overscrollBehavior: 'contain', p: 2,
                '& .MuiTypography-body2, & .MuiTypography-subtitle2, & .MuiAlert-message, & .MuiButton-root': {fontSize: 14, lineHeight: 1.55},
                '& .MuiTypography-caption': {fontSize: 12}}}>
                {!visible.length && <Stack spacing={2} sx={{pt: 1}}>
                    <Typography variant="subtitle2">{t('What should this workflow do?')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('Describe a change or attach a workflow screenshot. I can build the steps, connect them and ask about anything that needs clarifying.')}
                    </Typography>
                    <Stack alignItems="flex-start" gap={1}>
                        <Button size="small" variant="outlined" onClick={() => {chat.setText(t('Explain this workflow')); composer.current?.focus();}}
                            sx={{textTransform: 'none', textAlign: 'left'}}>{t('Explain this workflow')}</Button>
                    </Stack>
                </Stack>}
                <Stack spacing={3}>
                    {visible.map((message, index) => <Box key={message.id}>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.75}}>{message.role === 'user' ? t('You') : t('Agent')}</Typography>
                        {!!message.images?.length && <Box sx={{mb: 1}}>{thumbnails(message.images)}</Box>}
                        <Typography variant="body2" sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{message.text}</Typography>
                        {!!message.applied?.length && <Box sx={{mt: 1.5, pl: 1.5, borderLeft: 2, borderColor: 'success.main'}}>
                            <Typography variant="body2" sx={{fontWeight: 600}}>{t('Updated the draft')}</Typography>
                            {message.applied.map((change, i) => <Typography key={i} variant="body2" sx={{display: 'block'}}>{change}</Typography>)}
                            <Typography variant="caption" color="text.secondary">{t('Use Undo to reverse this response.')}</Typography>
                        </Box>}
                        {!!message.notes?.length && <Box sx={{mt: 1.5}}>
                            {message.notes.map((note, i) => <Typography key={i} variant="body2" color="text.secondary" sx={{mt: 0.75}}>{note}</Typography>)}
                        </Box>}
                        {message.questions?.map((question, i) => <Stack key={i} gap={1} sx={{mt: 2}}>
                            <Typography variant="body2" sx={{fontWeight: 600}}>{question.question}</Typography>
                            <Stack gap={0.75} alignItems="flex-start">
                                {question.options.map(option => <Button key={option} variant="outlined" size="small"
                                    disabled={!canEdit || chat.busy || index !== visible.length - 1}
                                    onClick={() => chooseAnswer(question.question, option)} sx={{textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start'}}>{option}</Button>)}
                            </Stack>
                        </Stack>)}
                    </Box>)}
                    {chat.proposal && <Box role="region" aria-label={t('Proposed changes')} sx={{border: 1, borderColor: 'primary.main', borderRadius: 1, p: 1.5}}>
                        <Typography variant="body2" sx={{fontWeight: 600}}>{t('Proposed changes')}</Typography>
                        {chat.proposal.summary.map((change, i) => <Typography key={i} variant="body2" sx={{display: 'block'}}>{change}</Typography>)}
                        <Typography variant="caption" color="text.secondary">{t('Nothing changes until you apply them.')}</Typography>
                        <Stack direction="row" gap={1} sx={{mt: 1.5}}>
                            <Button variant="contained" size="small" disabled={!canEdit || chat.busy} onClick={chat.applyProposal}>{t('Apply')}</Button>
                            <Button size="small" disabled={!canEdit || chat.busy} onClick={chat.discardProposal}>{t('Discard')}</Button>
                        </Stack>
                    </Box>}
                    {chat.busy && <Stack gap={0.75} role="status">
                        <Stack direction="row" alignItems="center" gap={1}><CircularProgress size={14}/>
                            <Typography variant="body2" color="text.secondary">{t('Working on your workflow…')}</Typography></Stack>
                        {!!chat.activity.length && <Box component="ol" sx={{m: 0, pl: 3.5, listStyle: 'none'}}>
                            {chat.activity.slice(-6).map((label, i) => <Typography key={`${i}-${label}`} component="li" variant="caption" color="text.secondary" sx={{display: 'block'}}>{label}</Typography>)}
                        </Box>}
                    </Stack>}
                    {!!chat.error && <Alert severity="error" action={chat.retry && <Button color="inherit" size="small" disabled={chat.busy || !canEdit} onClick={chat.retry}>{t('Retry')}</Button>}>{chat.error}</Alert>}
                </Stack>
            </Box>

            <Stack gap={1.25} sx={{p: 2, borderTop: 1, borderColor: 'divider'}}>
                {!!selectedLabels.length && <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{t('Selected:')}</Typography>
                    {selectedLabels.slice(0, 2).map((label, index) => <Chip key={index} label={label} size="small" sx={{maxWidth: 190}}/>)}
                    {selectedLabels.length > 2 && <Typography variant="body2">+{selectedLabels.length - 2}</Typography>}
                </Stack>}
                {!!chat.images.length && thumbnails(chat.images, true)}
                <TextField multiline minRows={3} maxRows={6} fullWidth inputRef={composer}
                           sx={{'& .MuiInputBase-root': {fontSize: 14, lineHeight: 1.5}}}
                           value={chat.text} onChange={event => chat.setText(event.target.value)} disabled={!canEdit || chat.busy}
                           placeholder={t('Describe a workflow or ask for a change…')}
                           inputProps={{'aria-label': t('Message the automation agent'), maxLength: 12000}}
                           onPaste={event => {
                               const files = Array.from(event.clipboardData.items).filter(item => item.kind === 'file').map(item => item.getAsFile()).filter((file): file is File => !!file);
                               if (files.length) {event.preventDefault(); void attachImages(files);}
                           }}
                           onKeyDown={event => {
                               if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {event.preventDefault(); if (canEdit) chat.send();}
                           }}/>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden
                           onChange={event => {void attachImages(Array.from(event.target.files || [])); event.target.value = '';}}/>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Tooltip title={t('Attach image')}><span><IconButton size="small" aria-label={t('Attach image')}
                            disabled={!canEdit || chat.busy || chat.readingImages} onClick={() => fileInput.current?.click()}>
                            {chat.readingImages ? <CircularProgress size={16}/> : <AddPhotoAlternateOutlinedIcon/>}
                        </IconButton></span></Tooltip>
                        <ToggleButtonGroup size="small" exclusive value={chat.mode} disabled={chat.busy} aria-label={t('How changes are applied')}
                                           onChange={(_event, value: 'apply' | 'propose' | null) => {if (value) chat.setMode(value);}}
                                           sx={{'& .MuiToggleButton-root': {textTransform: 'none', fontSize: 12, lineHeight: 1.4, py: 0.5, px: 1, whiteSpace: 'nowrap'}}}>
                            <ToggleButton value="apply">{t('Apply directly')}</ToggleButton>
                            <ToggleButton value="propose">{t('Ask first')}</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                    {chat.busy ? <Button size="small" startIcon={<StopRoundedIcon/>} onClick={chat.stop}>{t('Stop')}</Button> :
                        <Button variant="contained" size="small" endIcon={<SendOutlinedIcon/>} onClick={chat.send}
                                disabled={!canEdit || chat.readingImages || (!chat.text.trim() && !chat.images.length)}>{t('Send')}</Button>}
                </Stack>
            </Stack>
            <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 2}}><Box sx={{flex: 1, overflowWrap: 'anywhere'}}>{preview?.name}</Box>
                    <IconButton aria-label={t('Close image')} onClick={() => setPreview(null)}><CloseIcon/></IconButton></DialogTitle>
                <DialogContent>{preview && <Box component="img" src={imageSource(preview)} alt={preview.name} sx={{display: 'block', width: '100%', height: 'auto'}}/>}</DialogContent>
            </Dialog>
        </Box>
    );
}
